import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  NotebookPen,
  MessagesSquare,
  Upload,
  FolderKanban,
  Table2,
  GaugeCircle,
  LineChart,
  FileText,
  CheckSquare,
  ExternalLink,
  Lock,
  CalendarClock,
  CalendarSearch,
  Sparkles,
  Keyboard,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { shortcutHint } from "../mail/shortcuts";
import { useT } from "../i18n";

interface InstantHit {
  id: string;
  type: string;
  title: string;
  snippet: string;
  url: string;
  tags?: string[];
  owner?: string;
}

interface InstantResponse {
  hits: InstantHit[];
}

type Action = {
  id: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  run: () => void;
};

const TYPE_ICON: Record<string, React.ReactNode> = {
  note: <NotebookPen className="w-4 h-4" />,
  task: <CheckSquare className="w-4 h-4" />,
  document: <FileText className="w-4 h-4" />,
  sf_project: <FolderKanban className="w-4 h-4" />,
  sf_action: <CheckSquare className="w-4 h-4" />,
  chat_message: <MessagesSquare className="w-4 h-4" />,
};

export function CommandPalette({
  onClose,
  onQuickNote,
}: {
  onClose: () => void;
  onQuickNote: () => void;
}) {
  const pal = useT().shared.palette;
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<InstantHit[]>([]);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  /** Überschrift einer Treffer-Gruppe. `/instant` kann einen Typ liefern, den
   *  der Katalog (noch) nicht kennt — dann steht der rohe Typ da, so wie vorher
   *  auch (`TYPE_LABEL[type] ?? type`). */
  const gruppenLabel = (typ: string): string =>
    (pal.trefferTyp as Record<string, string | undefined>)[typ] ?? typ;

  // Mail-Ziele: wenn wir SCHON auf /mail sind, die bestehenden Query-Params
  // erhalten (v. a. `?m=` = offene Mail, `?tab=`) und nur den neuen Param setzen
  // — sonst würde `?do=termin` die offene Mail schließen und der do-Effect liefe
  // ins Leere. Von anderen Routen aus gibt es kein `?m=` zu erhalten (dort bleibt
  // der dokumentierte No-Op für termin/ki/vorlage akzeptabel). D7-Korrektur.
  const goMail = useCallback(
    (key: "do" | "tab", value: string) => {
      if (window.location.pathname === "/mail") {
        const sp = new URLSearchParams(window.location.search);
        sp.set(key, value);
        navigate({ pathname: "/mail", search: `?${sp.toString()}` });
      } else {
        navigate(`/mail?${key}=${value}`);
      }
    },
    [navigate]
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const h = setTimeout(async () => {
      if (!q.trim()) {
        setHits([]);
        return;
      }
      try {
        const res = await api.get<InstantResponse>(
          `/instant?q=${encodeURIComponent(q)}&limit=15`
        );
        setHits(res.hits || []);
        setSelected(0);
      } catch (e) {
        console.error("instant error", e);
        setHits([]);
      }
    }, 100);
    return () => clearTimeout(h);
  }, [q]);

  const actions: Action[] = useMemo(
    () => [
      {
        id: "quick-note",
        label: pal.aktionen.quickNote,
        hint: "N",
        icon: <Plus className="w-4 h-4" />,
        run: onQuickNote,
      },
      {
        id: "go-overview",
        label: pal.aktionen.uebersicht,
        icon: <GaugeCircle className="w-4 h-4" />,
        run: () => navigate("/"),
      },
      {
        id: "go-notes",
        label: pal.aktionen.notizen,
        icon: <NotebookPen className="w-4 h-4" />,
        run: () => navigate("/n"),
      },
      {
        id: "go-chat",
        label: pal.aktionen.frageStellen,
        icon: <MessagesSquare className="w-4 h-4" />,
        run: () => navigate("/chat"),
      },
      {
        id: "go-ingest",
        label: pal.aktionen.dokumentHochladen,
        icon: <Upload className="w-4 h-4" />,
        run: () => navigate("/docs"),
      },
      {
        id: "go-projects",
        label: pal.aktionen.projekte,
        icon: <FolderKanban className="w-4 h-4" />,
        run: () => navigate("/p"),
      },
      {
        id: "go-gantt",
        label: pal.aktionen.zeitachse,
        icon: <LineChart className="w-4 h-4" />,
        run: () => navigate("/tasks?view=zeitachse"),
      },
      {
        id: "go-datasets",
        label: pal.aktionen.tabellen,
        icon: <Table2 className="w-4 h-4" />,
        run: () => navigate("/ds"),
      },
      // ── Mail-Cockpit (X1) — reale Aktionen aus dem Mail-Modul. Cross-Page via
      // via `goMail` → `/mail?do=<key>` (bzw. `?tab=`); die Mail-Seite führt den
      // Param aus und räumt ihn weg. Auf /mail bleiben `?m=`/`?tab=` erhalten, sodass
      // termin/ki/vorlage die OFFENE Mail treffen (kein Schließen). `hint` kommt aus
      // shortcuts.ts (DRY). Von anderen Routen aus: dokumentierter No-Op ohne Mail.
      {
        id: "mail-search",
        label: pal.aktionen.mailSuche,
        hint: shortcutHint("search"),
        icon: <Search className="w-4 h-4" />,
        run: () => goMail("do", "search"),
      },
      {
        id: "mail-termin",
        label: pal.aktionen.mailTermin,
        hint: shortcutHint("termin"),
        icon: <CalendarClock className="w-4 h-4" />,
        run: () => goMail("do", "termin"),
      },
      {
        id: "mail-ki",
        label: pal.aktionen.mailKi,
        icon: <Sparkles className="w-4 h-4" />,
        run: () => goMail("do", "ki"),
      },
      {
        id: "mail-vorlage",
        label: pal.aktionen.mailVorlage,
        icon: <FileText className="w-4 h-4" />,
        run: () => goMail("do", "vorlage"),
      },
      {
        id: "mail-sf",
        label: pal.aktionen.mailSf,
        icon: <MessagesSquare className="w-4 h-4" />,
        run: () => goMail("tab", "fragen"),
      },
      {
        id: "mail-shortcuts",
        label: pal.aktionen.mailKuerzel,
        hint: shortcutHint("help"),
        icon: <Keyboard className="w-4 h-4" />,
        run: () => goMail("do", "help"),
      },
      {
        id: "go-calendar",
        label: pal.aktionen.kalender,
        icon: <CalendarClock className="w-4 h-4" />,
        run: () => navigate("/calendar"),
      },
      {
        id: "cal-find",
        label: pal.aktionen.kalenderTermin,
        icon: <CalendarSearch className="w-4 h-4" />,
        // `?do=find` → CalendarMs öffnet den FindMeetingWizard automatisch und
        // räumt den Param wieder weg (analog zum Mail-`?do=`-Muster). Auf
        // /calendar gibt es keine zu erhaltenden Params → einfacher navigate.
        run: () => navigate("/calendar?do=find"),
      },
    ],
    [navigate, onQuickNote, goMail, pal]
  );

  const filteredActions = useMemo(() => {
    if (!q) return actions;
    const ql = q.toLowerCase();
    return actions.filter((a) => a.label.toLowerCase().includes(ql));
  }, [q, actions]);

  // Gruppen für die Anzeige
  const grouped = useMemo(() => {
    const map = new Map<string, InstantHit[]>();
    for (const h of hits) {
      const arr = map.get(h.type) ?? [];
      arr.push(h);
      map.set(h.type, arr);
    }
    return [...map.entries()];
  }, [hits]);

  // Flacher Index für Tasten-Navigation
  const flatItems: Array<
    | { kind: "action"; action: Action }
    | { kind: "hit"; hit: InstantHit }
  > = useMemo(() => {
    const list: Array<
      | { kind: "action"; action: Action }
      | { kind: "hit"; hit: InstantHit }
    > = [];
    for (const a of filteredActions) list.push({ kind: "action", action: a });
    for (const [, arr] of grouped) {
      for (const h of arr) list.push({ kind: "hit", hit: h });
    }
    return list;
  }, [filteredActions, grouped]);

  const open = (item: (typeof flatItems)[number]) => {
    onClose();
    if (item.kind === "action") {
      item.action.run();
      return;
    }
    const h = item.hit;
    if (h.url.startsWith("http")) {
      window.open(h.url, "_blank", "noopener,noreferrer");
    } else if (h.type === "task") {
      navigate("/p");
    } else if (h.type === "note") {
      navigate("/n");
    } else {
      navigate("/docs");
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = flatItems[selected];
      if (it) open(it);
    }
  };

  let cursor = 0;
  return (
    <div
      data-command-palette
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-24 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-2xl w-[640px] max-w-[90vw] max-h-[70vh] flex flex-col overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 border-b border-border">
          <Search className="w-4 h-4 text-text-3" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={pal.platzhalter}
            className="w-full py-3 text-sm focus:outline-none bg-transparent"
          />
          <kbd className="text-[10px] text-text-3 border border-border rounded-md px-1 py-0.5">
            Esc
          </kbd>
        </div>
        <div className="overflow-y-auto py-1">
          {filteredActions.length > 0 && (
            <Group label={pal.gruppeAktionen}>
              {filteredActions.map((a) => {
                const i = cursor++;
                return (
                  <Row
                    key={a.id}
                    active={i === selected}
                    onClick={() => open({ kind: "action", action: a })}
                  >
                    {a.icon}
                    <span className="flex-1 text-sm">{a.label}</span>
                    {a.hint && (
                      <kbd className="text-[10px] text-text-3 border border-border rounded-md px-1 py-0.5">
                        {a.hint}
                      </kbd>
                    )}
                  </Row>
                );
              })}
            </Group>
          )}
          {grouped.map(([type, list]) => (
            <Group key={type} label={gruppenLabel(type)}>
              {list.map((h) => {
                const i = cursor++;
                return (
                  <Row
                    key={h.id}
                    active={i === selected}
                    onClick={() => open({ kind: "hit", hit: h })}
                  >
                    {TYPE_ICON[h.type] ?? <FileText className="w-4 h-4" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{h.title}</div>
                      {h.snippet && (
                        <div className="text-xs text-text-2 truncate">
                          {h.snippet}
                        </div>
                      )}
                    </div>
                    {h.owner === "salesforce" && (
                      <Lock className="w-3 h-3 text-warning" />
                    )}
                    {h.url.startsWith("http") && (
                      <ExternalLink className="w-3 h-3 text-text-3" />
                    )}
                  </Row>
                );
              })}
            </Group>
          ))}
          {q && hits.length === 0 && filteredActions.length === 0 && (
            <div className="text-sm text-text-2 text-center py-8">
              {pal.keineTreffer(q)}
            </div>
          )}
        </div>
        <div className="border-t border-border px-3 py-1.5 flex gap-3 text-[10px] text-text-2 bg-surface-2">
          <span>
            <kbd className="border border-border-strong rounded-md px-1">↑↓</kbd>{" "}
            {pal.fussNavigieren}
          </span>
          <span>
            <kbd className="border border-border-strong rounded-md px-1">↵</kbd>{" "}
            {pal.fussOeffnen}
          </span>
          <span>
            <kbd className="border border-border-strong rounded-md px-1">Esc</kbd>{" "}
            {pal.fussSchliessen}
          </span>
        </div>
      </div>
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-1">
      <div className="px-3 py-1 text-xs uppercase tracking-wider text-text-3">
        {label}
      </div>
      {children}
    </div>
  );
}

function Row({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "w-full flex items-center gap-3 px-3 py-2 text-left",
        active
          ? "bg-brand-50 dark:bg-brand-700/20"
          : "hover:bg-surface-2"
      )}
    >
      {children}
    </button>
  );
}
